// 白板: the shared whiteboard, over the map. Each 会议室 has one (the meeting toolbar's 白板 button opens it: meeting.js
// sends the window a 'deck-board' event), and a few stand on the open deck (the map's "whiteboards" layer, drawn by
// deck/whiteboards-view.js): walk up and press E, tap 「打开白板」, or click the board. Everyone who has a board open
// sees the others draw live; the office keeps it (src/office/whiteboard.ts; the wire is described there). Walking out
// of the room (or away from a deck board) closes it.
// Tools: 选择 (move, delete, double-click a note or text to edit), 拖动画布, 笔 (6 colours, 3 sizes), 橡皮 (whole
// strokes and shapes), 便签 (5 colours; drag them with 选择 or the 便签 tool), 文字, 矩形, 椭圆, 箭头; undo / redo (my
// own changes), zoom and pan (wheel, ctrl+wheel or a pinch, two fingers on a phone), 导出 PNG, and 清空 (a 会议室's
// board, by someone in the room, after a confirm). Keys while it's open: V H P E N T R O A pick a tool, Delete,
// ⌘/Ctrl+Z, ⌘/Ctrl+Shift+Z, Esc; everything else stays off the map (but M and Space, for the mic, go through).
// Two looks, switched at the end of the tools and kept per device (localStorage deck.wb.style): 像素风 (the default:
// Deck's cream panels and ink borders, a framed paper board, notes with ink outlines and pins, stepped ellipses and arrows) and
// 普通 (a plain white board). Both draw the same elements in the same boxes; 导出 PNG uses the look on screen.
import { icon } from '/icons.js';
import { t, tr, has, onLang, uiFont } from '/i18n.js';
import { PEN_COLORS, PEN_NAMES, NOTE_COLORS, NOTE_NAMES, SIZES, TEXT_SIZES, NOTE_W, NOTE_H, LIMITS, boardsOf, canUse, deckBoardNear, bounds, boundsAll } from '/whiteboard-core.js';

const LIVE_MS = 50, CURSOR_MS = 100, CURSOR_FADE_MS = 6000, MIN_S = 0.1, MAX_S = 5;
const NOTE_FONT = 16, NOTE_PAD = 12;
/** The office's refusals (wb_err reason): the words are wb.err.<reason>; one this page doesn't know is wb.err.invalid. */
const errText = (r) => t(r && has(`wb.err.${r}`) ? `wb.err.${r}` : 'wb.err.invalid');
const LOAD_MS = 20_000, RESYNC_MS = 1500; // a board that hasn't come in this long: say so; a resync waits this long after a refusal
const TOOLS = [['select', 'cursor', 'wb.tool.select', 'v'], ['hand', 'grab', 'wb.tool.hand', 'h'], ['pen', 'pencil', 'wb.tool.pen', 'p'], ['eraser', 'eraser', 'wb.tool.eraser', 'e'], ['note', 'note', 'wb.tool.note', 'n'],
  ['text', 'text', 'wb.tool.text', 't'], ['rect', 'rect', 'wb.tool.rect', 'r'], ['ellipse', 'ellipse', 'wb.tool.ellipse', 'o'], ['arrow', 'arrow', 'wb.tool.arrow', 'a']]; // [id, icon, its name's key, key]
const CURSOR_COLORS = ['#e76f51', '#2a9d8f', '#8e7dbe', '#f4a261', '#4f9dde', '#d16ba5', '#6a994e'];
const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const hash = (s) => { let h = 0; for (const c of String(s)) h = (h * 31 + c.codePointAt(0)) | 0; return Math.abs(h); };
const newId = () => (crypto.randomUUID?.() ?? String(Math.random()).slice(2) + Date.now()).replace(/-/g, '').slice(0, 16);
const typing = () => { const a = document.activeElement; return !!a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.isContentEditable); };

const CSS = `
#wb{position:fixed;inset:0;z-index:60;display:none;flex-direction:column;background:#efe9dc;color:#1b1410;font:14px/1.4 var(--ui-font,-apple-system,system-ui,sans-serif);
  padding:var(--st,0px) var(--sr,0px) var(--sb,0px) var(--sl,0px);box-sizing:border-box;touch-action:none;-webkit-user-select:none;user-select:none}
#wb.open{display:flex}
#wb button{font:inherit;color:inherit;cursor:pointer}
#wb .whead{display:flex;align-items:center;gap:6px;padding:8px 10px;background:#fff8e7;border-bottom:3px solid #1b1410;flex:none;flex-wrap:nowrap;overflow:hidden}
#wb .wtitle{font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;display:flex;align-items:center;gap:6px}
#wb .wviewers{display:flex;gap:4px;min-width:0;overflow:hidden;flex:1 1 auto}
#wb .chip{display:inline-flex;align-items:center;gap:4px;border:2px solid #1b1410;border-radius:999px;padding:0 8px;font-size:12px;white-space:nowrap;background:#fff}
#wb .chip i{width:8px;height:8px;border-radius:50%;display:inline-block}
#wb .hb{display:inline-flex;align-items:center;gap:4px;border:2px solid #1b1410;border-radius:10px;background:#fff;padding:4px 8px;white-space:nowrap}
#wb .hb:disabled{opacity:.4;cursor:default}
#wb .hb.close{background:#1b1410;color:#fff8e7}
#wb .hb.danger{color:#c92a2a}
#wb .wzoom{min-width:48px;justify-content:center;font-variant-numeric:tabular-nums}
#wb .wstage{position:relative;flex:1;min-height:0;overflow:hidden}
#wb canvas.wc{position:absolute;inset:0;width:100%;height:100%;touch-action:none;display:block}
#wb .wdock{position:absolute;left:50%;bottom:12px;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;gap:6px;max-width:calc(100% - 16px)}
#wb .wtools,#wb .wopts{display:flex;align-items:center;gap:4px;background:#fff8e7;border:3px solid #1b1410;border-radius:14px;padding:5px;max-width:100%;overflow-x:auto;scrollbar-width:none}
#wb .wtools::-webkit-scrollbar,#wb .wopts::-webkit-scrollbar{display:none}
#wb .wopts:empty{display:none}
#wb .tb{display:flex;flex-direction:column;align-items:center;gap:1px;border:2px solid transparent;border-radius:10px;background:none;padding:3px 6px;min-width:44px;font-size:11px;flex:none}
#wb .tb.on{background:#FDD03B;border-color:#1b1410}
#wb .sw{width:26px;height:26px;border-radius:50%;border:2px solid #1b1410;flex:none;padding:0}
#wb .sw.note{border-radius:5px}
#wb .sw.on{outline:3px solid #1b1410;outline-offset:2px}
#wb .sz{width:34px;height:30px;border:2px solid transparent;border-radius:8px;background:none;display:flex;align-items:center;justify-content:center;flex:none}
#wb .sz.on{border-color:#1b1410;background:#fff}
#wb .sz b{display:block;border-radius:50%;background:#1b1410}
#wb .sep{width:2px;height:24px;background:#1b1410;opacity:.2;flex:none;margin:0 2px}
#wb .ob{border:2px solid #1b1410;border-radius:8px;background:#fff;padding:3px 10px;white-space:nowrap;flex:none}
#wb .hint{font-size:12px;color:#5c4a3a;padding:0 6px;white-space:nowrap}
#wb textarea.wedit{position:absolute;border:2px dashed #1b1410;outline:none;resize:none;background:transparent;padding:0;margin:0;overflow:hidden;font-family:var(--ui-font,-apple-system,system-ui,sans-serif);line-height:1.25;color:#1b1410;-webkit-user-select:text;user-select:text;white-space:pre-wrap;word-break:break-all}
#wb .wtoast{position:absolute;left:50%;top:12px;transform:translateX(-50%);background:#1b1410;color:#fff8e7;border-radius:10px;padding:6px 12px;font-size:13px;display:none;pointer-events:none;z-index:3;max-width:90vw}
#wb .wmodal{position:absolute;inset:0;background:rgba(27,20,16,.45);display:none;align-items:center;justify-content:center;z-index:4}
#wb .wmodal .card{background:#fff8e7;border:3px solid #1b1410;border-radius:14px;padding:16px;max-width:320px;margin:12px}
#wb .wmodal .row{display:flex;gap:8px;justify-content:flex-end;margin-top:12px}
/* pixel (the default): Deck's own look, squarer corners and hard ink shadows, the board on a framed paper */
#wb:not(.plain) .whead{font-weight:600}
#wb:not(.plain) .hb,#wb:not(.plain) .ob{border-radius:4px;box-shadow:0 2px 0 #1b1410}
#wb:not(.plain) .hb:active,#wb:not(.plain) .ob:active{transform:translateY(2px);box-shadow:none}
#wb:not(.plain) .chip{border-radius:4px}
#wb:not(.plain) .wtools,#wb:not(.plain) .wopts{border-radius:6px;box-shadow:0 4px 0 #1b1410}
#wb:not(.plain) .tb{border-radius:4px;font-weight:600}
#wb:not(.plain) .sw{border-radius:3px}
#wb:not(.plain) .sz,#wb:not(.plain) .sz b{border-radius:2px}
#wb:not(.plain) .wstage{margin:9px;border:8px solid #b07a43;outline:3px solid #1b1410;
  border-image:repeating-linear-gradient(45deg,#b07a43 0 4px,#9a6634 4px 6px,#c08d55 6px 10px,#a8743e 10px 12px) 8}
#wb:not(.plain){background:#e9dcc0}
#wb:not(.plain) .wtoast,#wb:not(.plain) .wmodal .card{border-radius:4px}
/* the style switch, at the end of the tools */
#wb .wstyle{display:flex;flex:none;border:2px solid #1b1410;border-radius:4px;overflow:hidden;margin-left:2px}
#wb .wstyle button{border:0;background:#fff;padding:4px 7px;font-size:12px;white-space:nowrap}
#wb .wstyle button.on{background:#1b1410;color:#fff8e7}
/* plain: a plain whiteboard (white, thin grey lines, the system font, no frame) */
#wb.plain{background:#f1f3f5;color:#212529}
#wb.plain .whead{background:#fff;border-bottom:1px solid #dee2e6}
#wb.plain .hb,#wb.plain .ob,#wb.plain .chip{border:1px solid #ced4da;border-radius:8px}
#wb.plain .hb.close{background:#212529;color:#fff;border-color:#212529}
#wb.plain .wtools,#wb.plain .wopts{background:#fff;border:1px solid #dee2e6;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,.12)}
#wb.plain .tb.on{background:#e7f1ff;border-color:#74a7f0}
#wb.plain .sw{border:1px solid #adb5bd}
#wb.plain .sw.on{outline:2px solid #1c7ed6}
#wb.plain .sz.on{border-color:#74a7f0;background:#e7f1ff}
#wb.plain .wstyle{border:1px solid #ced4da;border-radius:8px}
#wb.plain .wstyle button.on{background:#1c7ed6;color:#fff}
#wb.plain .wmodal .card{border:1px solid #dee2e6;border-radius:12px;background:#fff}
#wb.plain .wtoast{background:#212529;border-radius:8px}
#wbPill{position:fixed;left:50%;transform:translateX(-50%);bottom:calc(var(--barh, calc(68px + var(--sb, 0px))) + 10px);z-index:30;display:none;align-items:center;gap:6px;
  font:600 14px var(--ui-font,-apple-system,system-ui,sans-serif);border:3px solid #1b1410;border-radius:999px;padding:6px 14px;background:#FDD03B;color:#1b1410;cursor:pointer;box-shadow:0 3px 0 #1b1410}
@media (max-width:640px){
  #wb .whead .lbl{display:none}
  #wb .whead{gap:4px;padding:6px}
  #wb .hb{padding:4px 6px}
  #wb .tb{min-width:38px;padding:3px 4px}
  #wb .tb .l{display:none}
  #wb .wviewers .chip:nth-child(n+3){display:none}
  #wb:not(.plain) .wstage{margin:5px;border-width:5px}
}`;

/**
 * @param {object} o
 * @param {(m) => void} o.send  @param {() => object|null} o.me  @param {object} o.MAP deck/map.js
 * @param {(x, y) => object|null} o.zoneAt the zone at an otter's position (collide.js)
 * @param {object} o.mover deck/move.js (clickAt: walk up to a deck board)
 * @param {() => void} [o.stopWalking]  @param {() => boolean} [o.blocked] the map is covered (the 会议室 view) or being edited (the map editor)
 */
export function initWhiteboard(o) {
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  // The boards on the map as it is now: o.MAP changes in place when the map editor's save arrives (app.js apply),
  // and a 会议室 made there has a board too (zone room-<id>).
  let boardsFor = null, boardsList = [];
  const boards = () => { if (boardsFor !== o.MAP.zones) { boardsFor = o.MAP.zones; boardsList = boardsOf(o.MAP); } return boardsList; };
  const zid = (x, y) => o.zoneAt(x, y)?.id ?? null;
  const mayUse = (b) => { const me = o.me(); return !!me && canUse(b, me.x, me.y, zid); };

  // ---- state
  let cur = null;           // the open board: {b, name, kind, clear, els: Map, viewers, seq}
  let order = [], orderDirty = true;
  const view = { x: 0, y: 0, s: 1 };  // the board point at the canvas' top-left, and CSS px per board unit
  let tool = 'pen', penC = 0, penW = 1, noteC = 0, textS = 1;
  const sel = new Set();
  let undo = [], redo = [];
  let draft = null;         // what I'm drawing: {k: 'path'|'rect'|'ellipse'|'arrow', ...} in board units
  let drag = null;          // moving the selection: {ids, x0, y0, dx, dy, orig: Map}
  let marquee = null;       // {x0, y0, x1, y1}
  const erasing = new Set(); let erasePt = null;
  const live = new Map();   // others' strokes being drawn: key `${peer}:${id}` -> {pts, c, w, er, at}
  const liveMv = new Map(); // others' drags: peer -> {ids: Set, dx, dy, at}
  const cursors = new Map(); // peer -> {x, y, name, at}
  let edit = null;          // the text box open: {ta, e (the element as it was, or null), k, x, y, c, s}
  let pendingOpen = null, pendingAt = 0, pendingRequest = 0, pendingVersion = 0;
  let dirty = true;
  // 像素风 / 普通: how the board looks for me (both show the same elements in the same places), kept on this device
  let style = (() => { try { return localStorage.getItem('deck.wb.style') === 'plain' ? 'plain' : 'pixel'; } catch { return 'pixel'; } })();

  // ---- the DOM
  const root = el('div'); root.id = 'wb';
  const head = el('div', 'whead'), title = el('span', 'wtitle'), viewersBox = el('div', 'wviewers');
  // key: its words (the label, also the tooltip); zoom's buttons show a sign and have only the tooltip
  const hb = (ic, key, fn, cls = '') => { const b = el('button', 'hb ' + cls); if (ic) b.append(icon(ic)); b.append(tr(el('span', 'lbl'), key)); tr(b, null, null, { title: key }); b.onclick = fn; return b; };
  const zb = (sign, key, fn) => { const b = el('button', 'hb '); b.append(el('span', '', sign)); tr(b, null, null, { title: key }); b.onclick = fn; return b; };
  const bUndo = hb('undo', 'wb.undo', () => doUndo()), bRedo = hb('redo', 'wb.redo', () => doRedo());
  const bOut = zb('−', 'wb.zoomOut', () => zoomBy(1 / 1.25));
  const bZoom = zb('100%', 'wb.fit', () => fit()); bZoom.classList.add('wzoom');
  const bIn = zb('+', 'wb.zoomIn', () => zoomBy(1.25));
  const bPng = hb('download', 'wb.export', () => exportPng());
  const bClear = hb('trash', 'wb.clear', () => askClear(), 'danger');
  const bClose = hb('close', 'wb.close', () => close(), 'close');
  head.append(title, viewersBox, bUndo, bRedo, bOut, bZoom, bIn, bPng, bClear, bClose);
  const stage = el('div', 'wstage'), canvas = el('canvas', 'wc'), g = canvas.getContext('2d');
  const dock = el('div', 'wdock'), opts = el('div', 'wopts'), tools = el('div', 'wtools');
  dock.append(opts, tools);
  const toastBox = el('div', 'wtoast');
  const modal = el('div', 'wmodal'), card = el('div', 'card');
  modal.append(card);
  stage.append(canvas, dock, toastBox, modal);
  root.append(head, stage);
  const pill = el('button'); pill.id = 'wbPill';
  document.body.append(root, pill);
  const toolBtns = new Map();
  for (const [id, ic] of TOOLS) {
    const b = el('button', 'tb'); b.append(icon(ic, { size: 32 }), el('span', 'l'));
    b.onclick = () => setTool(id); toolBtns.set(id, b); tools.append(b);
  }
  /** The tools' names and tooltips (「笔 (P)」), again on every language change. */
  function toolWords() { for (const [id, , name, key] of TOOLS) { const b = toolBtns.get(id); b.lastChild.textContent = t(name); b.title = t('wb.tool.tip', { tool: t(name), key: key.toUpperCase() }); } }
  toolWords();
  const styleBox = el('div', 'wstyle'), styleBtns = { pixel: tr(el('button'), 'wb.style.pixel', null, { title: 'wb.style.pixelTip' }), plain: tr(el('button'), 'wb.style.plain', null, { title: 'wb.style.plainTip' }) };
  for (const [k, b] of Object.entries(styleBtns)) { b.onclick = () => setStyle(k); styleBox.append(b); }
  tools.append(el('span', 'sep'), styleBox);
  function setStyle(k) {
    // the frame moves the board's corner: keep what's on screen where it was
    const corner = () => { const r = stage.getBoundingClientRect(); return [r.left + stage.clientLeft, r.top + stage.clientTop]; }, c0 = corner();
    style = k; try { localStorage.setItem('deck.wb.style', k); } catch {}
    root.classList.toggle('plain', k === 'plain');
    for (const [s, b] of Object.entries(styleBtns)) { b.classList.toggle('on', s === k); b.setAttribute('aria-pressed', String(s === k)); }
    const c1 = corner();
    if (cur) { view.x += (c1[0] - c0[0]) / view.s; view.y += (c1[1] - c0[1]) / view.s; }
    dirty = true;
  }
  setStyle(style);
  onLang(() => { toolWords(); pill.dataset.b = ''; dirty = true; if (cur) { renderHead(); renderTools(); } }); // dirty: the notes' 「改过」

  function toast(text) { toastBox.textContent = text; toastBox.style.display = 'block'; clearTimeout(toast.t); toast.t = setTimeout(() => (toastBox.style.display = 'none'), 3500); }
  function setTool(t) { commitEdit(); tool = t; if (t !== 'select') sel.clear(); renderTools(); dirty = true; }
  function renderTools() {
    for (const [id, b] of toolBtns) b.classList.toggle('on', id === tool);
    opts.replaceChildren();
    const swatches = (list, names, on, set, cls = '') => list.forEach((c, i) => { const b = el('button', 'sw ' + cls + (i === on ? ' on' : '')); b.style.background = c; b.title = t(`wb.color.${names[i]}`); b.onclick = () => { set(i); renderTools(); }; opts.append(b); });
    if (tool === 'pen' || tool === 'rect' || tool === 'ellipse' || tool === 'arrow') {
      swatches(PEN_COLORS, PEN_NAMES, penC, (i) => (penC = i));
      opts.append(el('span', 'sep'));
      SIZES.forEach((w, i) => { const b = el('button', 'sz' + (i === penW ? ' on' : '')), d = el('b'); d.style.width = d.style.height = 4 + i * 5 + 'px'; b.append(d); b.title = t(['wb.size.thin', 'wb.size.medium', 'wb.size.thick'][i]); b.onclick = () => { penW = i; renderTools(); }; opts.append(b); });
    } else if (tool === 'text') {
      swatches(PEN_COLORS, PEN_NAMES, penC, (i) => (penC = i));
      opts.append(el('span', 'sep'));
      TEXT_SIZES.forEach((s, i) => { const b = el('button', 'sz' + (i === textS ? ' on' : '')); b.textContent = 'A'; b.style.fontSize = 11 + i * 4 + 'px'; b.style.fontWeight = '700'; b.title = t(['wb.textSize.small', 'wb.textSize.medium', 'wb.textSize.large'][i]); b.onclick = () => { textS = i; renderTools(); }; opts.append(b); });
    } else if (tool === 'note') {
      swatches(NOTE_COLORS, NOTE_NAMES, noteC, (i) => (noteC = i), 'note');
      opts.append(el('span', 'hint', t('wb.hint.note')));
    } else if (tool === 'eraser') opts.append(el('span', 'hint', t('wb.hint.eraser')));
    else if (tool === 'select') {
      if (sel.size) {
        const notes = [...sel].map((id) => cur?.els.get(id)).filter((e) => e?.k === 'note');
        if (notes.length) { swatches(NOTE_COLORS, NOTE_NAMES, -1, (i) => recolor(notes, NOTE_COLORS[i]), 'note'); opts.append(el('span', 'sep')); }
        const b = el('button', 'ob'); b.append(icon('trash'), ' ' + t('wb.delete', { n: sel.size })); b.onclick = () => delSelection(); opts.append(b);
      } else opts.append(el('span', 'hint', t('wb.hint.select')));
    } else if (tool === 'hand') opts.append(el('span', 'hint', t('wb.hint.hand')));
  }
  function renderHead() {
    if (!cur) return;
    title.replaceChildren(icon('board'), el('span', '', cur.name));
    const me = o.me();
    viewersBox.replaceChildren(...cur.viewers.map((v) => { const c = el('span', 'chip'), dot = el('i'); dot.style.background = CURSOR_COLORS[hash(v.id) % CURSOR_COLORS.length]; c.append(dot, v.id === me?.id ? t('wb.you') : v.name); c.title = v.name; return c; }));
    bUndo.disabled = !undo.length; bRedo.disabled = !redo.length;
    bClear.style.display = cur.clear ? '' : 'none';
    bZoom.querySelector('span').textContent = Math.round(view.s * 100) + '%';
  }

  // ---- the board's elements
  const sorted = () => { if (orderDirty) { order = [...cur.els.values()].sort((a, b) => a.z - b.z || (a.id < b.id ? -1 : 1)); orderDirty = false; } return order; };
  function applyOp(op) {
    const els = cur.els;
    if (op.o === 'put') els.set(op.e.id, op.e);
    else if (op.o === 'mv') { const e = els.get(op.id); if (e) els.set(op.id, { ...e, x: op.x, y: op.y }); }
    else if (op.o === 'del') { els.delete(op.id); sel.delete(op.id); }
    else if (op.o === 'clear') { els.clear(); sel.clear(); }
    orderDirty = true; dirty = true;
  }
  /** Do ops now (here, and at the office), and remember how to take them back (inv) for 撤销. */
  function act(ops, inv) {
    if (!cur || !ops.length) return;
    for (const op of ops) applyOp(op);
    sendOps(ops);
    if (inv) { undo.push({ ops, inv }); if (undo.length > 100) undo.shift(); redo = []; }
    renderHead(); if (tool === 'select') renderTools();
  }
  function sendOps(ops) { for (let i = 0; i < ops.length; i += LIMITS.opsPerMsg) o.send({ t: 'wb_op', b: cur.b, ops: ops.slice(i, i + LIMITS.opsPerMsg) }); }
  const maxZ = () => { let z = 0; for (const e of cur.els.values()) if (e.z > z) z = e.z; return z; };
  const strip = (e) => { const { _w, ...rest } = e; return rest; };
  function create(e) { act([{ o: 'put', e: { ...e, z: maxZ() + 1, by: o.me()?.name } }], [{ o: 'del', id: e.id }]); }
  function doUndo() {
    const u = undo.pop(); if (!u || !cur) return;
    const fix = fixInv(u.inv); for (const op of fix) applyOp(op); sendOps(fix);
    redo.push(u); renderHead();
  }
  function doRedo() {
    const u = redo.pop(); if (!u || !cur) return;
    for (const op of u.ops) applyOp(op); sendOps(u.ops);
    undo.push(u); renderHead();
  }
  /** Undo's ops as they can still be done: moving or deleting what's gone meanwhile is skipped by the office anyway. */
  const fixInv = (ops) => ops.filter((op) => op.o === 'put' || cur.els.has(op.id));
  function delSelection() {
    const els = [...sel].map((id) => cur.els.get(id)).filter(Boolean);
    sel.clear();
    act(els.map((e) => ({ o: 'del', id: e.id })), els.map((e) => ({ o: 'put', e: strip(e) })));
  }
  function recolor(notes, c) { act(notes.map((e) => ({ o: 'put', e: { ...strip(e), c } })), notes.map((e) => ({ o: 'put', e: strip(e) }))); }

  // ---- drawing
  const measureCtx = document.createElement('canvas').getContext('2d');
  function wrap(text, font, maxW) {
    measureCtx.font = font; const out = [];
    for (const para of String(text).split('\n')) {
      let line = '';
      for (const ch of para) {
        if (measureCtx.measureText(line + ch).width <= maxW || !line) { line += ch; continue; }
        const sp = /\S/.test(ch) && /[\x21-\x7e]$/.test(line) ? line.lastIndexOf(' ') : -1;
        if (sp > 0) { out.push(line.slice(0, sp)); line = line.slice(sp + 1) + ch; } else { out.push(line); line = ch.trim(); }
      }
      out.push(line);
    }
    return out;
  }
  const textFont = (s) => `${s}px ${uiFont()}`; // (the edit box's font too: #wb textarea.wedit)
  /** An element's box, with a text's measured width. */
  function box(e) {
    if (e.k === 'arrow') { const b = bounds(e), m = Math.max(8, 2.5 * e.sw); return { x: b.x - m, y: b.y - m, w: b.w + 2 * m, h: b.h + 2 * m }; } // room for a 像素风 head
    if (e.k !== 'text') return bounds(e);
    if (!e._w) { measureCtx.font = textFont(e.s); e._w = Math.max(...String(e.text).split('\n').map((l) => measureCtx.measureText(l).width)) + 4; }
    return { x: e.x, y: e.y, w: e._w, h: String(e.text).split('\n').length * e.s * 1.25 };
  }
  function strokePath(c, x, y, pts) {
    c.beginPath();
    if (pts.length === 2) { c.moveTo(x + pts[0], y + pts[1]); c.lineTo(x + pts[0] + 0.01, y + pts[1]); return c.stroke(); }
    c.moveTo(x + pts[0], y + pts[1]);
    for (let i = 2; i < pts.length - 2; i += 2) { const mx = (pts[i] + pts[i + 2]) / 2, my = (pts[i + 1] + pts[i + 3]) / 2; c.quadraticCurveTo(x + pts[i], y + pts[i + 1], x + mx, y + my); }
    c.lineTo(x + pts[pts.length - 2], y + pts[pts.length - 1]);
    c.stroke();
  }
  function arrowHead(c, x1, y1, x2, y2, w) {
    const a = Math.atan2(y2 - y1, x2 - x1), L = Math.max(12, w * 3.2);
    c.beginPath(); c.moveTo(x2, y2); c.lineTo(x2 - L * Math.cos(a - 0.45), y2 - L * Math.sin(a - 0.45)); c.lineTo(x2 - L * Math.cos(a + 0.45), y2 - L * Math.sin(a + 0.45)); c.closePath(); c.fill();
  }
  /** An ellipse as pixel stairs: one quarter of it, its points snapped to a grid of q units around the centre and
   *  joined by steps whose corners are on the outside, mirrored four ways (so it is symmetric). The step is at most a
   *  sixth of the shape (a small ellipse is still a ring) and the quarter has at most 256 points (a huge one stays cheap). */
  function pixelEllipse(c, x, y, w, h, sw) {
    const rx = w / 2, ry = h / 2, cx = x + rx, cy = y + ry;
    const q = Math.max(0.5, Math.min(Math.max(4, sw * 1.5), Math.min(w, h) / 6)), snap = (v) => Math.round(v / q) * q;
    const n = Math.min(256, Math.max(4, Math.ceil((rx + ry) / q))), quarter = [];
    for (let i = 0; i <= n; i++) {
      const a = (i / n) * Math.PI / 2, px = snap(rx * Math.cos(a)), py = snap(ry * Math.sin(a)), last = quarter[quarter.length - 1];
      if (last && (last[0] !== px || last[1] !== py)) { if (last[0] !== px && last[1] !== py) quarter.push([last[0], py]); } // the corner further out
      if (!last || last[0] !== px || last[1] !== py) quarter.push([px, py]);
    }
    const pts = [...quarter, ...quarter.slice().reverse().map(([a, b]) => [-a, b]), ...quarter.map(([a, b]) => [-a, -b]), ...quarter.slice().reverse().map(([a, b]) => [a, -b])];
    c.beginPath(); c.moveTo(cx + pts[0][0], cy + pts[0][1]);
    for (const [a, b] of pts) c.lineTo(cx + a, cy + b);
    c.closePath(); c.stroke();
  }
  /** An arrow in pixels: square pixels of the pen's width on a grid from its start, the shaft a Bresenham line of
   *  them (4-connected: stairs) and the head a pixel triangle (5 pixels long or more, 4 for the thick pen, never longer than the arrow) around the tip's pixel, filled as one shape (no seams).
   *  A very long arrow gets bigger pixels (at most 1024 along it), so it stays cheap. */
  function pixelArrow(c, x1, y1, x2, y2, sw) {
    const len = Math.hypot(x2 - x1, y2 - y1); if (len < 0.5) return;
    const p = Math.max(2, sw, len / 1024), gx = Math.round((x2 - x1) / p), gy = Math.round((y2 - y1) / p);
    const cells = new Set(), put = (i, j) => cells.add(i + ',' + j);
    // the shaft (Bresenham)
    let i = 0, j = 0; const ax = Math.abs(gx), ay = Math.abs(gy), sx = Math.sign(gx), sy = Math.sign(gy); let err = ax - ay;
    for (;;) { put(i, j); if (i === gx && j === gy) break; const e2 = 2 * err; if (e2 > -ay) { err -= ay; i += sx; if (e2 < ax) put(i, j); } if (e2 < ax) { err += ax; j += sy; } } // a diagonal move fills the corner: stairs, not dots
    // the head: cells whose centres fall in a triangle pointing along the arrow, tip at the last cell
    const gl = Math.hypot(gx, gy), ux = (x2 - x1) / len, uy = (y2 - y1) / len, Lc = Math.max(Math.min(2, gl), Math.min(gl, Math.max(p >= 10 ? 4 : 5, Math.round(Math.max(12, sw * 3.2) / p)))), r = Math.ceil(Lc) + 1;
    for (let a = -r; a <= r; a++) for (let b = -r; b <= r; b++) {
      const u = a * ux + b * uy, v = -a * uy + b * ux; // along (towards the tip), across
      if (u <= 0.3 && u >= -Lc && Math.abs(v) <= -u * 0.6 + 0.5) put(gx + a, gy + b);
    }
    c.beginPath();
    for (const k of cells) { const [a, b] = k.split(',').map(Number); c.rect(x1 + a * p - p / 2, y1 + b * p - p / 2, p, p); }
    c.fill();
  }
  /** A darker shade of a #rrggbb colour (the band at a pixel note's top). */
  const shade = (hex, f) => '#' + [1, 3, 5].map((i) => Math.round(parseInt(hex.slice(i, i + 2), 16) * f).toString(16).padStart(2, '0')).join('');
  /** Draw one element on context c (already in board units); dx, dy: a drag in progress. The two styles draw the same
   *  elements over the same boxes; only edges, corners and shadows differ (hand-drawn strokes are smooth in both). */
  function drawEl(c, e, dx = 0, dy = 0) {
    const x = e.x + dx, y = e.y + dy, px = style === 'pixel';
    c.lineCap = 'round'; c.lineJoin = 'round';
    if (e.k === 'path') { c.strokeStyle = e.c; c.lineWidth = e.w; strokePath(c, x, y, e.pts); return; }
    if (px) { c.lineCap = 'square'; c.lineJoin = 'miter'; }
    if (e.k === 'rect') { c.strokeStyle = e.c; c.lineWidth = e.sw; c.strokeRect(x, y, e.w, e.h); }
    else if (e.k === 'ellipse') {
      c.strokeStyle = e.c; c.lineWidth = e.sw;
      if (px) pixelEllipse(c, x, y, e.w, e.h, e.sw);
      else { c.beginPath(); c.ellipse(x + e.w / 2, y + e.h / 2, e.w / 2, e.h / 2, 0, 0, Math.PI * 2); c.stroke(); }
    }
    else if (e.k === 'arrow') {
      c.strokeStyle = c.fillStyle = e.c; c.lineWidth = e.sw;
      if (px) pixelArrow(c, x, y, x + e.dx, y + e.dy, e.sw);
      else { c.beginPath(); c.moveTo(x, y); c.lineTo(x + e.dx, y + e.dy); c.stroke(); arrowHead(c, x, y, x + e.dx, y + e.dy, e.sw); }
    }
    else if (e.k === 'text') {
      c.fillStyle = e.c; c.font = textFont(e.s); c.textBaseline = 'top';
      String(e.text).split('\n').forEach((l, i) => c.fillText(l, x + 2, y + i * e.s * 1.25 + e.s * 0.1));
    } else if (e.k === 'note') {
      if (px) {
        // a pixel note: a hard ink shadow, an ink outline, a darker band and a pin
        c.fillStyle = 'rgba(27,20,16,0.35)'; c.fillRect(x + 6, y + 6, e.w, e.h);
        c.fillStyle = e.c; c.fillRect(x, y, e.w, e.h);
        c.fillStyle = shade(e.c, 0.88); c.fillRect(x, y, e.w, 10);
        c.strokeStyle = '#1b1410'; c.lineWidth = 3; c.strokeRect(x + 1.5, y + 1.5, e.w - 3, e.h - 3);
        c.fillStyle = '#1b1410'; c.fillRect(x + e.w / 2 - 6, y - 4, 12, 12);
        c.fillStyle = '#e03131'; c.fillRect(x + e.w / 2 - 3, y - 1, 6, 6);
      } else {
        c.save(); c.shadowColor = 'rgba(0,0,0,0.16)'; c.shadowBlur = 14; c.shadowOffsetY = 4;
        c.fillStyle = e.c; c.beginPath(); if (c.roundRect) c.roundRect(x, y, e.w, e.h, 6); else c.rect(x, y, e.w, e.h); c.fill(); c.restore();
      }
      if (edit?.e?.id === e.id) return; // the text box is over it
      const font = `${NOTE_FONT}px ${uiFont()}`;
      c.fillStyle = '#1b1410'; c.font = font; c.textBaseline = 'top';
      const lines = wrap(e.text, font, e.w - NOTE_PAD * 2), maxL = Math.max(1, Math.floor((e.h - NOTE_PAD * 2 - 14) / (NOTE_FONT * 1.3)));
      lines.slice(0, maxL).forEach((l, i) => c.fillText(i === maxL - 1 && lines.length > maxL ? l.slice(0, -1) + '…' : l, x + NOTE_PAD, y + NOTE_PAD + i * NOTE_FONT * 1.3));
      // who made it, and who last changed it when that's someone else
      if (e.by) { c.font = `11px ${uiFont()}`; c.fillStyle = 'rgba(27,20,16,0.55)'; c.fillText(e.ed ? t('wb.edited', { by: e.by, ed: e.ed }) : e.by, x + NOTE_PAD, y + e.h - NOTE_PAD - 9); }
    }
  }
  // The board's ground: 像素风 a warm paper with pixel fibres (a tile, moving with the board), 普通 plain white.
  let fibres = null;
  function paper(c, W, H, ox, oy) {
    if (style === 'plain') { c.fillStyle = '#ffffff'; c.fillRect(0, 0, W, H); return; }
    c.fillStyle = '#f7eedb'; c.fillRect(0, 0, W, H);
    if (!fibres) {
      const t = document.createElement('canvas'); t.width = t.height = 96; const tc = t.getContext('2d');
      let seed = 7; const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
      for (let i = 0; i < 260; i++) { tc.fillStyle = rnd() < 0.6 ? 'rgba(150,110,60,0.10)' : 'rgba(255,255,255,0.45)'; tc.fillRect(Math.floor(rnd() * 48) * 2, Math.floor(rnd() * 48) * 2, rnd() < 0.3 ? 4 : 2, 2); }
      fibres = c.createPattern(t, 'repeat');
    }
    c.save(); c.imageSmoothingEnabled = false; c.translate(-Math.round(((ox % 96) + 96) % 96), -Math.round(((oy % 96) + 96) % 96)); c.fillStyle = fibres; c.fillRect(0, 0, W + 96, H + 96); c.restore();
  }
  function frame() {
    requestAnimationFrame(frame);
    if (!cur) return;
    const now = performance.now();
    for (const [k, v] of cursors) if (now - v.at > CURSOR_FADE_MS) { cursors.delete(k); dirty = true; }
    for (const [k, v] of live) if (now - v.at > 10_000) { live.delete(k); dirty = true; }
    if (!dirty) return;
    dirty = false;
    const dpr = Math.min(2, devicePixelRatio || 1), W = stage.clientWidth, H = stage.clientHeight;
    if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) { canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr); }
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    paper(g, W, H, view.x * view.s, view.y * view.s);
    // the grid (every 40 units; sparser when zoomed out): round dots, or small pixel crosses on the paper
    let step = 40; while (step * view.s < 16) step *= 2;
    const gx0 = Math.floor(view.x / step) * step, gy0 = Math.floor(view.y / step) * step, r = Math.max(1, Math.min(2, view.s * 1.5));
    g.fillStyle = style === 'pixel' ? 'rgba(120,84,40,0.22)' : 'rgba(33,37,41,0.14)';
    for (let gx = gx0; gx < view.x + W / view.s; gx += step) for (let gy = gy0; gy < view.y + H / view.s; gy += step) {
      const sx = Math.round((gx - view.x) * view.s), sy = Math.round((gy - view.y) * view.s);
      if (style === 'pixel') { g.fillRect(sx - 3, sy - 1, 6, 2); g.fillRect(sx - 1, sy - 3, 2, 6); }
      else { g.beginPath(); g.arc(sx, sy, r, 0, 7); g.fill(); }
    }
    g.save(); g.scale(view.s, view.s); g.translate(-view.x, -view.y);
    const vx0 = view.x, vy0 = view.y, vx1 = view.x + W / view.s, vy1 = view.y + H / view.s;
    const moving = new Map(); for (const v of liveMv.values()) for (const id of v.ids) moving.set(id, v);
    for (const e of sorted()) {
      if (erasing.has(e.id)) continue;
      const m = drag?.ids.has(e.id) ? drag : moving.get(e.id), dx = m?.dx ?? 0, dy = m?.dy ?? 0, bx = box(e);
      if (bx.x + dx > vx1 || bx.y + dy > vy1 || bx.x + bx.w + dx < vx0 || bx.y + bx.h + dy < vy0) continue;
      drawEl(g, e, dx, dy);
    }
    for (const v of live.values()) { g.lineCap = 'round'; g.lineJoin = 'round'; g.strokeStyle = v.c; g.lineWidth = v.w; if (v.pts.length >= 2) strokePath(g, 0, 0, v.pts); }
    if (draft) drawEl(g, draft);
    if (edit && !edit.e && edit.k === 'note') drawEl(g, { k: 'note', x: edit.x, y: edit.y, w: NOTE_W, h: NOTE_H, c: edit.c, text: '' });
    // the selection
    g.lineWidth = 2 / view.s; g.strokeStyle = '#1c7ed6'; g.setLineDash([6 / view.s, 4 / view.s]);
    for (const id of sel) { const e = cur.els.get(id); if (!e) continue; const b = box(e), d = drag?.ids.has(id) ? drag : null, p = 6 / view.s; g.strokeRect(b.x + (d?.dx ?? 0) - p, b.y + (d?.dy ?? 0) - p, b.w + 2 * p, b.h + 2 * p); }
    if (marquee) { g.fillStyle = 'rgba(28,126,214,0.08)'; const x = Math.min(marquee.x0, marquee.x1), y = Math.min(marquee.y0, marquee.y1), w = Math.abs(marquee.x1 - marquee.x0), h = Math.abs(marquee.y1 - marquee.y0); g.fillRect(x, y, w, h); g.strokeRect(x, y, w, h); }
    g.setLineDash([]);
    if (tool === 'eraser' && erasePt) { g.strokeStyle = 'rgba(27,20,16,0.5)'; g.lineWidth = 1.5 / view.s; g.beginPath(); g.arc(erasePt.x, erasePt.y, 10 / view.s, 0, 7); g.stroke(); }
    g.restore();
    // the others' cursors, with their names
    for (const [id, v] of cursors) {
      const sx = (v.x - view.x) * view.s, sy = (v.y - view.y) * view.s, col = CURSOR_COLORS[hash(id) % CURSOR_COLORS.length];
      if (sx < -20 || sy < -20 || sx > W + 20 || sy > H + 20) continue;
      g.fillStyle = col; g.strokeStyle = '#fff'; g.lineWidth = 1.5;
      g.beginPath(); g.moveTo(sx, sy); g.lineTo(sx + 2, sy + 15); g.lineTo(sx + 6, sy + 11); g.lineTo(sx + 12, sy + 11); g.closePath(); g.fill(); g.stroke();
      g.font = `600 12px ${uiFont()}`; g.textBaseline = 'top';
      const tw = g.measureText(v.name).width; g.fillRect(sx + 10, sy + 14, tw + 10, 18); g.fillStyle = '#fff'; g.fillText(v.name, sx + 15, sy + 17);
    }
  }
  requestAnimationFrame(frame);
  addEventListener('resize', () => { dirty = true; });

  // ---- the view
  const toBoard = (sx, sy) => ({ x: view.x + sx / view.s, y: view.y + sy / view.s });
  function zoomAt(f, sx, sy) {
    const s = Math.max(MIN_S, Math.min(MAX_S, view.s * f)), p = toBoard(sx, sy);
    view.s = s; view.x = p.x - sx / s; view.y = p.y - sy / s; dirty = true; renderHead();
  }
  const zoomBy = (f) => zoomAt(f, stage.clientWidth / 2, stage.clientHeight / 2);
  function fit() {
    const W = stage.clientWidth || innerWidth, H = (stage.clientHeight || innerHeight) - 110; // room for the tools at the bottom
    const bx = cur && boundsAll(cur.els.values());
    if (!bx) { view.s = 1; view.x = -W / 2; view.y = -(H / 2); }
    else {
      const s = Math.max(MIN_S, Math.min(1, (W - 60) / Math.max(1, bx.w), (H - 60) / Math.max(1, bx.h)));
      view.s = s; view.x = bx.x + bx.w / 2 - W / 2 / s; view.y = bx.y + bx.h / 2 - H / 2 / s;
    }
    dirty = true; renderHead();
  }

  // ---- hit testing (board units)
  function segDist(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay, L = dx * dx + dy * dy, t = L ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / L)) : 0;
    return Math.hypot(px - ax - t * dx, py - ay - t * dy);
  }
  function hits(e, x, y, tol) {
    const b = box(e);
    if (x < b.x - tol || y < b.y - tol || x > b.x + b.w + tol || y > b.y + b.h + tol) return false;
    if (e.k === 'note' || e.k === 'text') return true;
    if (e.k === 'path') { const p = e.pts; if (p.length === 2) return Math.hypot(x - e.x - p[0], y - e.y - p[1]) <= tol + e.w / 2; for (let i = 0; i + 3 < p.length; i += 2) if (segDist(x, y, e.x + p[i], e.y + p[i + 1], e.x + p[i + 2], e.y + p[i + 3]) <= tol + e.w / 2) return true; return false; }
    if (e.k === 'arrow') return segDist(x, y, e.x, e.y, e.x + e.dx, e.y + e.dy) <= tol + e.sw;
    if (e.k === 'rect') { const i = x > e.x + tol && x < e.x + e.w - tol && y > e.y + tol && y < e.y + e.h - tol; return !i || tool === 'select'; }
    if (e.k === 'ellipse') { const rx = e.w / 2, ry = e.h / 2, n = Math.hypot((x - e.x - rx) / rx, (y - e.y - ry) / ry); return tool === 'select' ? n <= 1 + tol / Math.min(rx, ry) : Math.abs(n - 1) * Math.min(rx, ry) <= tol + e.sw; }
    return false;
  }
  function topAt(x, y, tol, kinds) { const list = sorted(); for (let i = list.length - 1; i >= 0; i--) { const e = list[i]; if ((!kinds || kinds.includes(e.k)) && hits(e, x, y, tol)) return e; } return null; }

  // ---- live: what I'm drawing, my cursor
  let liveQ = null, liveT = 0, curQ = null, curT = 0;
  function liveSend(now = performance.now()) {
    if (!cur) return;
    const m = { t: 'wb_live', b: cur.b };
    if (liveQ && (now - liveT >= LIVE_MS || liveQ.flush)) { if (liveQ.path?.pts.length || liveQ.mv || liveQ.end) Object.assign(m, liveQ.path?.pts.length ? { path: liveQ.path } : {}, liveQ.mv ? { mv: liveQ.mv } : {}, liveQ.end ? { end: liveQ.end } : {}); liveQ = null; liveT = now; }
    if (curQ && now - curT >= CURSOR_MS) { m.cur = curQ; curQ = null; curT = now; }
    if (Object.keys(m).length > 2) o.send(m);
  }
  setInterval(() => liveSend(), LIVE_MS);
  const queuePts = (pts, first) => { if (!liveQ?.path) liveQ = { ...(liveQ || {}), path: { id: draft.id, pts: [], c: draft.c, w: draft.w, s: first } }; liveQ.path.pts.push(...pts); if (liveQ.path.pts.length >= 300) liveSend(performance.now() + LIVE_MS); };

  // ---- the pointer
  const ptrs = new Map(); let gesture = null, lastTap = null, panning = null;
  const local = (e) => { const r = canvas.getBoundingClientRect(); return { sx: e.clientX - r.left, sy: e.clientY - r.top }; };
  canvas.addEventListener('pointerdown', (e) => {
    if (!cur) return;
    commitEdit();
    canvas.setPointerCapture(e.pointerId);
    const { sx, sy } = local(e);
    ptrs.set(e.pointerId, { sx, sy, type: e.pointerType });
    if (ptrs.size === 2) { // a second finger: pan and pinch; whatever the first one started is dropped
      cancelGesture();
      const [a, b] = [...ptrs.values()];
      gesture = { d: Math.hypot(a.sx - b.sx, a.sy - b.sy) || 1, mx: (a.sx + b.sx) / 2, my: (a.sy + b.sy) / 2 };
      return;
    }
    if (ptrs.size > 2) return;
    const p = toBoard(sx, sy);
    if (tool === 'hand' || e.button === 1 || (e.pointerType === 'mouse' && e.buttons === 4)) { panning = { sx, sy, vx: view.x, vy: view.y }; return; }
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    startTool(p, e);
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!cur) return;
    const { sx, sy } = local(e), p = toBoard(sx, sy);
    if (e.pointerType === 'mouse' || ptrs.has(e.pointerId)) { curQ = [Math.round(p.x), Math.round(p.y)]; }
    if (tool === 'eraser') { erasePt = p; dirty = true; }
    if (!ptrs.has(e.pointerId)) return;
    ptrs.set(e.pointerId, { ...ptrs.get(e.pointerId), sx, sy });
    if (gesture && ptrs.size >= 2) {
      const [a, b] = [...ptrs.values()], d = Math.hypot(a.sx - b.sx, a.sy - b.sy) || 1, mx = (a.sx + b.sx) / 2, my = (a.sy + b.sy) / 2;
      view.x -= (mx - gesture.mx) / view.s; view.y -= (my - gesture.my) / view.s;
      zoomAt(d / gesture.d, mx, my);
      gesture.d = d; gesture.mx = mx; gesture.my = my; return;
    }
    if (panning) { view.x = panning.vx - (sx - panning.sx) / view.s; view.y = panning.vy - (sy - panning.sy) / view.s; dirty = true; return; }
    moveTool(p, e);
  });
  const up = (e) => {
    if (!ptrs.delete(e.pointerId)) return;
    if (gesture) { if (ptrs.size < 2) gesture = null; return; }
    if (panning) { panning = null; return; }
    const { sx, sy } = local(e);
    endTool(toBoard(sx, sy), e, e.type === 'pointercancel');
  };
  canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);
  canvas.addEventListener('pointerleave', () => { erasePt = null; dirty = true; });
  canvas.addEventListener('wheel', (e) => {
    if (!cur) return; e.preventDefault();
    const { sx, sy } = local(e), k = e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 400 : 1;
    if (e.ctrlKey || e.metaKey) zoomAt(Math.exp(-Math.max(-100, Math.min(100, e.deltaY * k)) / 200), sx, sy);
    else { view.x += (e.deltaX * k) / view.s; view.y += (e.deltaY * k) / view.s; dirty = true; }
  }, { passive: false });
  canvas.addEventListener('dblclick', (e) => { if (!cur) return; const { sx, sy } = local(e), p = toBoard(sx, sy), t = topAt(p.x, p.y, 4 / view.s, ['note', 'text']); if (t) openEdit(t); });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());

  function cancelGesture() {
    if (draft?.k === 'path' && liveQ !== undefined) { liveQ = { ...(liveQ || {}), end: draft.id, flush: true }; liveSend(); }
    draft = null; marquee = null; erasing.clear();
    if (drag) { liveQ = { ...(liveQ || {}), end: 'mv', flush: true }; liveSend(); drag = null; }
    dirty = true;
  }

  let down = null; // {p, t, moved}
  function startTool(p, e) {
    down = { p, t: performance.now(), moved: false };
    const tol = 6 / view.s;
    if (tool === 'pen') { draft = { id: newId(), k: 'path', x: Math.round(p.x), y: Math.round(p.y), c: PEN_COLORS[penC], w: SIZES[penW], pts: [0, 0] }; queuePts([draft.x, draft.y], true); }
    else if (tool === 'rect' || tool === 'ellipse') draft = { id: newId(), k: tool, x: p.x, y: p.y, w: 0, h: 0, c: PEN_COLORS[penC], sw: SIZES[penW], ox: p.x, oy: p.y };
    else if (tool === 'arrow') draft = { id: newId(), k: 'arrow', x: p.x, y: p.y, dx: 0, dy: 0, c: PEN_COLORS[penC], sw: SIZES[penW] };
    else if (tool === 'eraser') { erasing.clear(); erasePt = p; eraseAt(p, p); }
    else if (tool === 'select' || tool === 'note') {
      const t = topAt(p.x, p.y, tol, tool === 'note' ? ['note'] : null);
      if (t) {
        if (tool === 'select' && !sel.has(t.id)) { if (!e.shiftKey) sel.clear(); sel.add(t.id); renderTools(); }
        const ids = tool === 'select' ? new Set(sel) : new Set([t.id]);
        drag = { ids, x0: p.x, y0: p.y, dx: 0, dy: 0, hit: t };
      } else if (tool === 'select') { if (!e.shiftKey) sel.clear(); marquee = { x0: p.x, y0: p.y, x1: p.x, y1: p.y }; renderTools(); }
    }
    dirty = true;
  }
  function eraseAt(a, b) {
    const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / (4 / view.s)));
    for (let i = 0; i <= n; i++) {
      const x = a.x + ((b.x - a.x) * i) / n, y = a.y + ((b.y - a.y) * i) / n;
      for (const e of sorted()) if (!erasing.has(e.id) && hits(e, x, y, 10 / view.s)) erasing.add(e.id);
    }
  }
  let lastP = null;
  function moveTool(p) {
    if (!down) return;
    if (!down.moved && Math.hypot(p.x - down.p.x, p.y - down.p.y) * view.s > 4) down.moved = true;
    if (draft?.k === 'path') {
      const px = Math.round(p.x) - draft.x, py = Math.round(p.y) - draft.y, n = draft.pts.length;
      if (Math.hypot(px - draft.pts[n - 2], py - draft.pts[n - 1]) * view.s < 1.5) return;
      draft.pts.push(px, py); queuePts([Math.round(p.x), Math.round(p.y)], false);
      if (draft.pts.length >= LIMITS.ptsMax * 2) { // a long stroke goes in pieces; the next starts where this one ends
        const done = draft; finishPath(done);
        draft = { ...done, id: newId(), x: done.x + px, y: done.y + py, pts: [0, 0] }; queuePts([draft.x, draft.y], true);
      }
    } else if (draft && (draft.k === 'rect' || draft.k === 'ellipse')) { draft.x = Math.min(draft.ox, p.x); draft.y = Math.min(draft.oy, p.y); draft.w = Math.abs(p.x - draft.ox); draft.h = Math.abs(p.y - draft.oy); }
    else if (draft?.k === 'arrow') { draft.dx = p.x - draft.x; draft.dy = p.y - draft.y; }
    else if (tool === 'eraser') eraseAt(lastP ?? p, p);
    else if (drag && down.moved) { drag.dx = p.x - drag.x0; drag.dy = p.y - drag.y0; liveQ = { ...(liveQ || {}), mv: { ids: [...drag.ids].slice(0, 200), dx: drag.dx, dy: drag.dy } }; }
    else if (marquee) { marquee.x1 = p.x; marquee.y1 = p.y; }
    lastP = p; dirty = true;
  }
  function finishPath(d) {
    const { id, k, x, y, c, w, pts } = d;
    liveQ = { ...(liveQ || {}), flush: true }; liveSend();
    create({ id, k, x, y, c, w, pts });
  }
  function endTool(p, e, cancelled) {
    const d = down; down = null; lastP = null;
    if (!d) return;
    if (cancelled) { cancelGesture(); return; }
    const tap = !d.moved && performance.now() - d.t < 500;
    if (draft?.k === 'path') { const dd = draft; draft = null; finishPath(dd); }
    else if (draft && (draft.k === 'rect' || draft.k === 'ellipse')) { const { ox, oy, ...dd } = draft; draft = null; if (dd.w * view.s >= 4 && dd.h * view.s >= 4) create(dd); }
    else if (draft?.k === 'arrow') { const dd = draft; draft = null; if (Math.hypot(dd.dx, dd.dy) * view.s >= 8) create(dd); }
    else if (tool === 'eraser') {
      const els = [...erasing].map((id) => cur.els.get(id)).filter(Boolean); erasing.clear();
      if (els.length) act(els.map((x) => ({ o: 'del', id: x.id })), els.map((x) => ({ o: 'put', e: strip(x) })));
    } else if (drag) {
      const dg = drag; drag = null;
      if (dg.dx || dg.dy) {
        const els = [...dg.ids].map((id) => cur.els.get(id)).filter(Boolean);
        liveQ = { ...(liveQ || {}), mv: null, end: 'mv', flush: true }; liveSend();
        act(els.map((x) => ({ o: 'mv', id: x.id, x: Math.round(x.x + dg.dx), y: Math.round(x.y + dg.dy) })), els.map((x) => ({ o: 'mv', id: x.id, x: x.x, y: x.y })));
      } else if (tap) {
        const now = performance.now(), dbl = lastTap && lastTap.id === dg.hit.id && now - lastTap.t < 450;
        lastTap = { id: dg.hit.id, t: now };
        if (tool === 'note' || dbl) { if (dg.hit.k === 'note' || dg.hit.k === 'text') openEdit(dg.hit); }
      }
    } else if (marquee) {
      const m = marquee; marquee = null;
      const x0 = Math.min(m.x0, m.x1), y0 = Math.min(m.y0, m.y1), x1 = Math.max(m.x0, m.x1), y1 = Math.max(m.y0, m.y1);
      if (x1 - x0 > 2 || y1 - y0 > 2) for (const el2 of sorted()) { const b = box(el2); if (b.x < x1 && b.x + b.w > x0 && b.y < y1 && b.y + b.h > y0) sel.add(el2.id); }
      renderTools();
    } else if (tool === 'note' && tap) openEdit(null, 'note', p.x - NOTE_W / 2, p.y - NOTE_H / 2);
    else if (tool === 'text' && tap) { const t = topAt(p.x, p.y, 4 / view.s, ['text']); if (t) openEdit(t); else openEdit(null, 'text', p.x, p.y - TEXT_SIZES[textS] * 0.6); }
    dirty = true;
  }

  // ---- the text box for notes and text
  function openEdit(e, k = e?.k, x = e?.x, y = e?.y) {
    commitEdit();
    const ta = el('textarea', 'wedit'); ta.maxLength = LIMITS.text; ta.spellcheck = false;
    edit = { ta, e, k, x, y, c: e?.c ?? (k === 'note' ? NOTE_COLORS[noteC] : PEN_COLORS[penC]), s: e?.s ?? TEXT_SIZES[textS] };
    ta.value = e?.text ?? '';
    ta.placeholder = t(k === 'note' ? 'wb.placeholder.note' : 'wb.placeholder.text');
    place();
    ta.addEventListener('input', place);
    ta.addEventListener('keydown', (ev) => {
      ev.stopPropagation();
      if (ev.isComposing || ev.keyCode === 229) return;
      if (ev.key === 'Escape') { ev.preventDefault(); const was = edit; edit = null; ta.remove(); dirty = true; void was; }
      else if (ev.key === 'Enter' && (k === 'text' ? !ev.shiftKey : ev.metaKey || ev.ctrlKey)) { ev.preventDefault(); commitEdit(); }
    });
    ta.addEventListener('keyup', (ev) => ev.stopPropagation());
    ta.addEventListener('blur', () => setTimeout(() => { if (edit?.ta === ta) commitEdit(); }, 0));
    stage.append(ta); ta.focus(); ta.select();
    dirty = true;
  }
  function place() {
    if (!edit) return;
    const { ta, k, x, y } = edit, s = view.s;
    if (k === 'note') {
      const w = edit.e?.w ?? NOTE_W, h = edit.e?.h ?? NOTE_H;
      Object.assign(ta.style, { left: (x - view.x + NOTE_PAD) * s + 'px', top: (y - view.y + NOTE_PAD) * s + 'px', width: (w - NOTE_PAD * 2) * s + 'px', height: (h - NOTE_PAD * 2) * s + 'px', fontSize: NOTE_FONT * s + 'px', lineHeight: '1.3' });
    } else {
      measureCtx.font = textFont(edit.s);
      const lines = (ta.value || ta.placeholder).split('\n'), w = Math.max(...lines.map((l) => measureCtx.measureText(l).width)) + edit.s;
      Object.assign(ta.style, { left: (x - view.x) * s + 'px', top: (y - view.y) * s + 'px', width: Math.max(60, w * s) + 'px', height: lines.length * edit.s * 1.25 * s + 6 + 'px', fontSize: edit.s * s + 'px', color: edit.c });
    }
  }
  function commitEdit() {
    if (!edit) return;
    const { ta, e, k, x, y, c, s } = edit; edit = null; ta.remove(); dirty = true;
    if (!cur) return;
    const text = ta.value.replace(/\s+$/, '');
    if (e) {
      const now = cur.els.get(e.id); if (!now || now.text === text) return;
      if (!text && k === 'text') act([{ o: 'del', id: e.id }], [{ o: 'put', e: strip(now) }]);
      else act([{ o: 'put', e: { ...strip(now), text } }], [{ o: 'put', e: strip(now) }]);
    } else if (k === 'note' && text.trim()) create({ id: newId(), k: 'note', x: Math.round(x), y: Math.round(y), w: NOTE_W, h: NOTE_H, c, text });
    else if (text.trim()) create({ id: newId(), k: 'text', x: Math.round(x), y: Math.round(y), c, s, text });
  }

  // ---- 清空 (a room's board) and 导出 PNG
  function askClear() {
    if (!cur?.clear) return;
    card.replaceChildren(tr(el('h3'), 'wb.clearAsk.title'), tr(el('p'), 'wb.clearAsk.body'));
    const row = el('div', 'row'), no = tr(el('button', 'hb'), 'wb.clearAsk.cancel'), yes = tr(el('button', 'hb close'), 'wb.clear');
    no.onclick = () => (modal.style.display = 'none');
    yes.onclick = () => { modal.style.display = 'none'; if (!cur) return; applyOp({ o: 'clear' }); sendOps([{ o: 'clear' }]); undo = []; redo = []; renderHead(); renderTools(); };
    row.append(no, yes); card.append(row); modal.style.display = 'flex';
  }
  function exportPng() {
    if (!cur) return;
    const list = sorted(), bx = boundsAll(list) ?? { x: 0, y: 0, w: 400, h: 300 };
    const pad = 40, k = Math.min(2, 4096 / (bx.w + pad * 2), 4096 / (bx.h + pad * 2));
    const c = document.createElement('canvas'); c.width = Math.ceil((bx.w + pad * 2) * k); c.height = Math.ceil((bx.h + pad * 2) * k);
    const cx = c.getContext('2d'); paper(cx, c.width, c.height, 0, 0); // the ground of the style I'm looking at
    cx.scale(k, k); cx.translate(pad - bx.x, pad - bx.y);
    for (const e of list) drawEl(cx, e);
    c.toBlob((blob) => {
      if (!blob) return toast(t('wb.exportFailed'));
      const a = el('a'); a.href = URL.createObjectURL(blob); a.download = `${cur?.name ?? t('wb.board')}-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.png`;
      document.body.append(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
      toast(t('wb.exported'));
    }, 'image/png');
  }

  // ---- keys: while the board is open they're the board's (but M and Space, the mic's, go on to the page)
  function key(e) {
    if (!cur || modal.style.display === 'flex') return;
    if (typing() || e.target.closest?.('.dd-list')) return; // the text box (or chat) types; deck/input.js ignores keys then too; an open dropdown has its own keys
    const k = e.key.toLowerCase(), mod = e.metaKey || e.ctrlKey;
    if (!mod && (k === 'm' || k === ' ')) return;
    e.stopPropagation();
    if (e.type !== 'keydown') return;
    if (mod && k === 'z') { e.preventDefault(); e.shiftKey ? doRedo() : doUndo(); return; }
    if (mod && k === 'y') { e.preventDefault(); doRedo(); return; }
    if (mod) return;
    if (k === 'escape') { if (sel.size) { sel.clear(); renderTools(); dirty = true; } else close(); return; }
    if ((k === 'delete' || k === 'backspace') && sel.size) { e.preventDefault(); delSelection(); return; }
    if (k === '+' || k === '=') return zoomBy(1.25);
    if (k === '-') return zoomBy(0.8);
    const t = TOOLS.find((x) => x[3] === k); if (t) setTool(t[0]);
  }
  addEventListener('keydown', key, true); addEventListener('keyup', key, true);

  // ---- opening and closing
  function open(id) {
    const b = boards().find((x) => x.id === id); if (!b) return;
    if (!mayUse(b)) { toast(t(b.kind === 'room' ? 'wb.open.room' : 'wb.open.near')); return; }
    pendingOpen = null; pendingRequest++; o.stopWalking?.();
    if (cur?.b !== b.id) { cur = { b: b.id, name: b.name, kind: b.kind, clear: b.kind === 'room', els: new Map(), viewers: [], seq: 0, loading: true }; undo = []; redo = []; sel.clear(); live.clear(); liveMv.clear(); cursors.clear(); orderDirty = true; }
    root.classList.add('open'); document.body.classList.add('wb-open');
    pill.style.display = 'none';
    o.send({ t: 'wb_open', b: b.id }); waitLoad();
    renderHead(); renderTools(); dirty = true;
  }
  function close(quiet) {
    if (!cur) return;
    commitEdit(); cancelGesture();
    if (!quiet) o.send({ t: 'wb_close' });
    cur = null; root.classList.remove('open'); document.body.classList.remove('wb-open'); modal.style.display = 'none';
  }
  addEventListener('deck-board', (e) => { const r = e.detail?.room ?? o.zoneAt(o.me()?.x ?? -1, o.me()?.y ?? -1)?.id; if (r) open(r); });

  /** The deck board I can open now, if any. */
  const near = () => { const me = o.me(); return me ? deckBoardNear(boards(), me.x, me.y, zid) : null; };
  pill.onclick = () => { const b = near(); if (b) open(b.id); };
  setInterval(() => {
    const me = o.me(), b = !cur && me && !o.blocked?.() ? near() : null;
    if (b) { if (pill.dataset.b !== b.id) { pill.replaceChildren(icon('board'), ' ' + t('wb.pill', { name: b.name })); pill.dataset.b = b.id; pill.title = t('wb.pillTip'); } pill.style.display = 'flex'; }
    else pill.style.display = 'none';
    // walked up to a board I clicked: open it
    if (pendingOpen && !cur) {
      const pb = boards().find((x) => x.id === pendingOpen);
      if (o.mover.intentVersion() !== pendingVersion) pendingOpen = null;
      else if (pb && mayUse(pb)) { pendingOpen = null; open(pb.id); }
      else if (performance.now() - pendingAt > 30_000 || (me && !me.moving && !o.mover.planning() && performance.now() - pendingAt > 1500)) pendingOpen = null;
    }
  }, 250);

  // ---- the office
  // Loading: the board comes in parts (wb_snap part 0..parts-1, in order); if it isn't all here in LOAD_MS, say so.
  let loadTimer = 0, parts = null, resyncTimer = 0;
  function waitLoad() {
    clearTimeout(loadTimer);
    loadTimer = setTimeout(() => {
      if (!cur || (!cur.loading && !cur.resyncing)) return;
      if (cur.loading) { close(true); o.toast?.(t('wb.load.failed')); } else { cur.resyncing = false; toast(t('wb.load.resyncFailed')); }
    }, LOAD_MS);
  }
  /** Back in step with the office after a refusal: one reload, however many refusals came in meanwhile. */
  function resync() {
    if (!cur || cur.loading || cur.resyncing || resyncTimer) return;
    resyncTimer = setTimeout(() => { resyncTimer = 0; if (!cur || cur.loading) return; cur.resyncing = true; o.send({ t: 'wb_open', b: cur.b, sync: true }); waitLoad(); }, RESYNC_MS);
  }
  function msg(m) {
    if (m.t === 'wb_snap') {
      if (!cur || cur.b !== m.b) return;
      if (m.part === 0 || m.part == null) parts = { meta: m, els: [] };
      if (!parts) return;
      parts.els.push(...m.els);
      if (m.parts != null && m.part < m.parts - 1) return; // more to come
      const meta = parts.meta, els = parts.els; parts = null; clearTimeout(loadTimer);
      const first = cur.loading;
      cur.els = new Map(els.map((e) => [e.id, e])); cur.seq = meta.seq; cur.viewers = meta.viewers ?? []; cur.clear = !!meta.clear; cur.name = meta.name ?? cur.name; cur.loading = false; cur.resyncing = false;
      if (meta.readOnly) toast(errText('broken'));
      for (const id of [...sel]) if (!cur.els.has(id)) sel.delete(id);
      orderDirty = true; dirty = true;
      if (first) fit();
      renderHead(); renderTools();
    } else if (m.t === 'wb_ops') {
      if (!cur || cur.b !== m.b || cur.loading) return;
      for (const op of m.ops) {
        applyOp(op);
        if (op.o === 'put') { live.delete(`${m.by}:${op.e.id}`); }
        if (op.o === 'clear' && m.by !== o.me()?.id) { undo = []; redo = []; live.clear(); toast(t('wb.cleared', { name: m.name })); }
      }
      if (m.ops.some((op) => op.o === 'mv')) liveMv.delete(m.by);
      cur.seq = m.seq; renderHead();
    } else if (m.t === 'wb_live') {
      if (!cur || cur.b !== m.b) return;
      const now = performance.now();
      if (m.cur) cursors.set(m.id, { x: m.cur[0], y: m.cur[1], name: m.name, at: now });
      if (m.path) {
        const k = `${m.id}:${m.path.id}`; let v = live.get(k);
        if (!v || m.path.s) { v = { pts: [], c: m.path.c, w: m.path.w, at: now }; live.set(k, v); }
        v.pts.push(...m.path.pts); v.at = now;
        const n = m.path.pts.length; if (n >= 2) cursors.set(m.id, { x: m.path.pts[n - 2], y: m.path.pts[n - 1], name: m.name, at: now });
      }
      if (m.mv) liveMv.set(m.id, { ids: new Set(m.mv.ids), dx: m.mv.dx, dy: m.mv.dy, at: now });
      if (m.end) { if (m.end === 'mv') liveMv.delete(m.id); else live.delete(`${m.id}:${m.end}`); }
      dirty = true;
    } else if (m.t === 'wb_viewers') {
      if (!cur || cur.b !== m.b) return;
      cur.viewers = m.viewers ?? [];
      const ids = new Set(cur.viewers.map((v) => v.id));
      for (const id of cursors.keys()) if (!ids.has(id)) cursors.delete(id);
      for (const k of live.keys()) if (!ids.has(k.split(':')[0])) live.delete(k);
      renderHead(); dirty = true;
    } else if (m.t === 'wb_gone') {
      if (cur?.b === m.b) { close(true); o.toast?.(t(m.deleted ? 'wb.gone.deleted' : m.quiet ? 'wb.gone.quiet' : 'wb.gone.away')); }
    } else if (m.t === 'wb_err') {
      if (!cur || (m.b && cur.b !== m.b)) { if (m.reason === 'where') o.toast?.(errText('where')); return; }
      toast(errText(m.reason));
      if (m.reason === 'where') { if (cur.loading || !mayUse(boards().find((x) => x.id === cur.b))) close(true); return; }
      if (m.reason === 'broken') return; // read-only: what's shown is what's there
      if (cur.loading) { close(true); return; } // opening was refused (too many opens)
      resync(); // what I did locally may not have happened: one reload brings the board back in step
    }
  }

  function boardAt(wx, wy) { return boards().find((x) => x.kind === 'deck' && wx >= x.x - 1.3 && wx <= x.x + 1.3 && wy >= x.y - 2.5 && wy <= x.y + 0.2) ?? null; }
  return {
    msg,
    /** E on the map: open the deck board I'm next to (true: handled, so I don't also sit down). */
    interact() { const b = !cur && !o.blocked?.() && near(); if (!b) return false; open(b.id); return true; },
    /** A click on the map (tiles): on a deck board, open it, or walk up to it first. */
    mapClick(wx, wy) {
      const b = boardAt(wx, wy);
      if (!b) return false;
      const request = ++pendingRequest; pendingOpen = null;
      if (mayUse(b)) open(b.id);
      else {
        const planning = o.mover.clickAt(b.x, b.y + 1), version = o.mover.intentVersion();
        void planning.then((started) => {
          if (started && pendingRequest === request && o.mover.intentVersion() === version) {
            pendingOpen = b.id; pendingAt = performance.now(); pendingVersion = version;
          }
        });
      }
      return true;
    },
    /** The deck board drawn at map point (wx, wy) tiles, or null (public/pick.js selects it). */
    boardAt,
    /** The deck board whose sign lights up (deck/whiteboards-view.js). */
    near: () => near()?.id ?? null,
    /** A (re)connected office was just welcomed: it knows nothing of my open board, so open it again. */
    welcome() { if (cur) { cur.loading = true; parts = null; o.send({ t: 'wb_open', b: cur.b }); waitLoad(); } },
    /** The page stops (another tab took over). */
    stop() { pendingRequest++; pendingOpen = null; close(true); },
    isOpen: () => !!cur,
  };
}
